import * as Yup from 'yup';

import { Button, Chip, Responsive, Text } from '@components';
import {
  ApiEndpoint,
  Dictionaries,
  FormActionType,
  FormType,
  InputMod,
  InputType,
  ProfileEndpoint,
  ServiceModalName,
  Translation,
} from '@enums';
import i18n from '@translations';
import type { EndpointType, FormConfig, FormConfigMapping } from '@types';
import { getOptionListFromCatalog } from '@utils';

interface GetProfileFormConfigParams {
  endpoint?: EndpointType;
  schema?: Yup.AnySchema;
  isVerified?: boolean;
  email?: string;
  username?: string;
  handleChangeUsername?: () => void;
  handleChangePassword?: () => void;
  handleModeChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleLanguageChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

export const getProfileFormConfig = ({
  endpoint = ApiEndpoint.DEFAULT,
  schema,
  isVerified,
  email,
  username,
  handleChangeUsername,
  handleChangePassword,
  handleModeChange,
  handleLanguageChange,
}: GetProfileFormConfigParams): FormConfig => {
  const tModal: (key: string) => string = (key: string): string =>
    i18n.t(`${Translation.ProfileSettings}:modals.${key}`);

  const tForm: (key: string) => string = (key: string): string => i18n.t(`${Translation.ProfileSettings}:forms.${key}`);

  const tButton: (key: string) => string = (key: string): string =>
    i18n.t(`${Translation.ProfileSettings}:buttons.${key}`);

  const tTab: (key: string) => string = (key: string): string => i18n.t(`${Translation.SignIn}:tab.${key}`);

  const tCommon: (key: string) => string = (key: string): string => i18n.t(`${Translation.Common}:${key}`);

  const tPTabs: (key: string) => string = (key: string): string => i18n.t(`${Translation.ProfileSettings}:tabs.${key}`);

  const PROFILE_FORM: FormConfigMapping = {
    [ApiEndpoint.DEFAULT]: {
      formType: FormType.Profile,
      inputList: [
        {
          name: 'email',
          label: (
            <div className="flex justify-between gap-3">
              {tPTabs('emailAddress')}
              <Responsive showBelow="sm">
                <Chip
                  className="rounded-md font-medium"
                  color={isVerified ? 'green' : 'red'}
                  adornment={tCommon(`verificationStatus.${isVerified ? 'VERIFIED' : 'UNVERIFIED'}`)}
                />
              </Responsive>
            </div>
          ),
          description: (
            <div className="flex space-x-3 items-center">
              <Text $level={4} color="text-black" className="flex-grow truncate">
                {email}
              </Text>
              <Responsive showAbove="sm">
                <Chip
                  className="rounded-md font-medium"
                  color={isVerified ? 'green' : 'red'}
                  adornment={tCommon(`verificationStatus.${isVerified ? 'VERIFIED' : 'UNVERIFIED'}`)}
                />
              </Responsive>
            </div>
          ),
          children: (
            <Button onClick={() => {}} disabled className="rounded-lg w-full" predefinedColor="zinc">
              {tButton('changeEmailAddress')}
            </Button>
          ),
          inputType: InputType.Custom,
          withoutLabel: true,
          required: true,
        },
        {
          name: 'username',
          label: tPTabs('username'),
          description: (
            <Text $level={4} color="text-black" className="flex-grow truncate">
              {username}
            </Text>
          ),
          children: (
            <Button onClick={handleChangeUsername} disabled className="rounded-lg w-full" predefinedColor="zinc">
              {tButton('changeUsername')}
            </Button>
          ),
          inputType: InputType.Custom,
          withoutLabel: true,
          required: true,
        },
        {
          name: 'password',
          label: tPTabs('password'),
          description: tPTabs('passwordDescription'),
          children: (
            <Button onClick={handleChangePassword} className="rounded-lg w-full">
              {tButton('changePassword')}
            </Button>
          ),
          inputType: InputType.Custom,
          withoutLabel: true,
          required: true,
        },
        {
          name: 'displayMode',
          title: tPTabs('appearance'),
          label: tForm('displayMode'),
          description: tPTabs('appearanceDescription'),
          placeholder: tForm('selectYourDisplayMode'),
          inputType: InputType.Select,
          inputMod: InputMod.Filled,
          withoutLabel: true,
          required: true,
          options: getOptionListFromCatalog({ name: Dictionaries.DisplayMode }),
          onChange: handleModeChange,
        },
        {
          name: 'language',
          title: tPTabs('languagePreference'),
          label: tForm('language'),
          description: tPTabs('languageDescription'),
          placeholder: tForm('selectYourLanguagePreference'),
          inputType: InputType.Select,
          inputMod: InputMod.Filled,
          withoutLabel: true,
          required: true,
          options: getOptionListFromCatalog({ name: Dictionaries.Languages }),
          onChange: handleLanguageChange,
        },
      ],
    },
    [ProfileEndpoint.CHANGE_USERNAME]: {
      formType: FormType.Profile,
      endpoint: ProfileEndpoint.CHANGE_USERNAME,
      formActionType: FormActionType.Edit,
      modalName: ServiceModalName.FormModal,
      label: {
        title: tModal('changeYourUsername'),
        description: tModal('changeUsernameDescription'),
        confirmButton: tButton('changeUsername'),
      },
      inputList: [
        {
          name: 'first_name',
          defaultValue: '',
          inputType: InputType.Text,
          required: true,
          placeholder: tForm('enterYourNewFirstName'),
          label: tForm('firstName'),
        },
        {
          name: 'last_name',
          defaultValue: '',
          inputType: InputType.Text,
          required: true,
          placeholder: tForm('enterYourNewLastName'),
          label: tForm('lastName'),
        },
      ],
      validationSchema: schema,
    },
    [ProfileEndpoint.CHANGE_PASSWORD]: {
      formType: FormType.Profile,
      endpoint: ProfileEndpoint.CHANGE_PASSWORD,
      formActionType: FormActionType.Edit,
      modalName: ServiceModalName.FormModal,
      label: {
        title: tModal('changeYourPassword'),
        description: tTab('createANewPasswordForYourAccount'),
        confirmButton: tButton('changePassword'),
      },
      inputList: [
        {
          name: 'current_password',
          defaultValue: '',
          inputType: InputType.Password,
          required: true,
          placeholder: tForm('enterYourCurrentPassword'),
          label: tForm('currentPassword'),
        },
        {
          name: 'password',
          defaultValue: '',
          inputType: InputType.Password,
          required: true,
          placeholder: tForm('enterYourNewPassword'),
          label: tForm('newPassword'),
        },
        {
          name: 'confirm_password',
          defaultValue: '',
          inputType: InputType.Password,
          required: true,
          placeholder: tForm('enterYourConfirmPassword'),
          label: tForm('confirmPassword'),
        },
      ],
      validationSchema: schema,
    },
  };

  return (PROFILE_FORM[endpoint] ?? PROFILE_FORM[ApiEndpoint.DEFAULT]) as FormConfig;
};
